/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useMemo } from "react";
import PropTypes from "prop-types";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import Avatar from "../../../shared/v2/Avatar";
import { XSolidV6, TriangleExclamationSolidV6 } from "../../../shared/v2/Icomoon";
import DragHandle from "../../../shared/v2/DragHandle";
import NoUserSelectedAlert from "./NoUserSelectedAlert";

const RoundRobinAgent =
  ({ agents, removeAgent }) =>
  // Props come from react-beautiful-dnd
  // eslint-disable-next-line react/prop-types
  ({ innerRef, draggableProps, dragHandleProps }, { isDragging }, { source }) => {
    const agent = agents[source.index];

    return (
      <div
        ref={innerRef}
        className={isDragging ? "tw-rounded-4px tw-shadow-dragging tw-bg-white" : ""}
        {...draggableProps}
      >
        <div className="tw-flex tw-flex-row tw-items-center tw-whitespace-nowrap">
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <div {...dragHandleProps}>
            <DragHandle orientation="vertical" />
          </div>
          <Avatar className="tw-mx-8px" size="medium" src={agent.avatarUrl} alt={agent.name} />
          <span className="tw-overflow-ellipsis tw-overflow-x-hidden tw-mr-12px tw-font-normal">
            {agent.name} - {agent.routingNumber}
          </span>
          {agent.routingNumber === null && (
            <span className="tw-flex tw-items-center tw-px-2 tw-py-1 tw-rounded tw-gap-[6px] tw-text-[#A81E19] tw-bg-[#FAE6E6]">
              <TriangleExclamationSolidV6 />
              No routing number
            </span>
          )}
          <div className="tw-ml-auto tw-cursor-pointer" onClick={() => removeAgent(agent.id)}>
            <XSolidV6 />
          </div>
        </div>
      </div>
    );
  };

RoundRobinAgent.propTypes = {
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      avatarUrl: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      routingNumber: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  removeAgent: PropTypes.func.isRequired,
};

const reorder = (array, oldIndex, newIndex) => {
  const result = Array.from(array);
  const [removed] = result.splice(oldIndex, 1);
  result.splice(newIndex, 0, removed);

  return result;
};

const RoundRobin = ({ agents, setAgents }) => {
  const removeAgent = (id) => {
    setAgents(agents.filter((agent) => agent.id !== id));
  };

  const onSortEnd = ({ source, destination }) => {
    if (!destination) {
      return;
    }
    if (destination.index === source.index) {
      return;
    }
    setAgents(reorder(agents, source.index, destination.index));
  };

  const renderRoundRobinAgent = useMemo(
    () =>
      RoundRobinAgent({
        agents,
        removeAgent,
      }),
    [agents],
  );

  return agents?.length ? (
    <DragDropContext onDragEnd={onSortEnd}>
      <Droppable droppableId="round-robin-list" renderClone={renderRoundRobinAgent}>
        {({ innerRef, droppableProps, placeholder }) => (
          <div ref={innerRef} {...droppableProps}>
            <div className="tw-flex tw-flex-col tw-gap-24px">
              {agents.length > 0 &&
                agents.map((agent, i) => (
                  <Draggable index={i} draggableId={`draggable-${agent.id}`} key={agent.id}>
                    {renderRoundRobinAgent}
                  </Draggable>
                ))}
            </div>

            {placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  ) : (
    <NoUserSelectedAlert />
  );
};

RoundRobin.propTypes = {
  agents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setAgents: PropTypes.func.isRequired,
};

export default RoundRobin;
